import { useState } from 'react';
import { ButtonBase, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { selectUser } from 'features/auth/store/authSelectors';
import { useAppSelector } from 'features/store/hooks';
import { AccountMenu } from './AccountMenu';
import { InitialsAvatar } from './InitialsAvatar';

type NavProfileProps = {
  /** Fade out the text block (collapsed rail keeps only the avatar). */
  expanded: boolean;
  /** Reports the account menu's open state, so a collapsed rail can stay
   *  expanded while it's open. */
  onMenuOpenChange: (open: boolean) => void;
};

/** Dock footer: avatar + name + email; opens the account menu upward. */
export const NavProfile = ({ expanded, onMenuOpenChange }: NavProfileProps) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const closeMenu = () => {
    setAnchorEl(null);
    onMenuOpenChange(false);
  };

  return (
    <>
      <ButtonBase
        onClick={event => {
          setAnchorEl(event.currentTarget);
          onMenuOpenChange(true);
        }}
        aria-label={t('ShellAccountButtonLabel')}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={theme => ({
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1.5,
          p: '8px 6px',
          borderRadius: '10px',
          minHeight: 54,
          '&:hover': { backgroundColor: 'action.hover' },
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <InitialsAvatar size={38} />
        <Box
          sx={{
            minWidth: 0,
            textAlign: 'left',
            opacity: expanded ? 1 : 0,
            transition: 'opacity 150ms',
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
            {user.displayName ?? ''}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.tertiary' }} noWrap>
            {user.email ?? ''}
          </Typography>
        </Box>
      </ButtonBase>
      <AccountMenu
        variant="popover"
        placement="up"
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
      />
    </>
  );
};
